<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import ContactCardsAPI from 'dashboard/api/whatsappContactCards';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({ conversationId: { type: Number, required: true } });
const { t } = useI18n();
const dialog = ref(null);
const query = ref('');
const results = ref([]);
const name = ref('');
const phone = ref('');
const sending = ref(false);
const searching = ref(false);
const searched = ref(false);
let searchRevision = 0;
const valid = computed(
  () =>
    name.value.trim() &&
    /^\+[1-9][0-9]{1,14}$/.test(phone.value.replace(/[ ()-]/g, ''))
);

function reset() {
  searchRevision += 1;
  query.value = '';
  results.value = [];
  name.value = '';
  phone.value = '';
  searching.value = false;
  searched.value = false;
}

watch(
  () => props.conversationId,
  () => dialog.value?.close()
);

async function search() {
  if (sending.value) return;
  const revision = ++searchRevision;
  searched.value = false;
  if (!query.value.trim()) {
    results.value = [];
    searching.value = false;
    return;
  }
  searching.value = true;
  try {
    const response = await ContactsAPI.search(query.value.trim());
    if (revision === searchRevision) {
      results.value = response.data.payload.filter(
        contact => contact.phone_number
      );
      searched.value = true;
    }
  } catch {
    if (revision === searchRevision)
      useAlert(t('WHATSAPP_CONTACT_CARDS.SEARCH_ERROR'));
  } finally {
    if (revision === searchRevision) searching.value = false;
  }
}

function choose(contact) {
  name.value = contact.name;
  phone.value = contact.phone_number;
  results.value = [];
  searched.value = false;
}

async function send() {
  if (!valid.value || sending.value) return;
  sending.value = true;
  try {
    await ContactCardsAPI.send(props.conversationId, [
      {
        name: name.value.trim(),
        phones: [{ number: phone.value, label: 'CELL' }],
      },
    ]);
    useAlert(t('WHATSAPP_CONTACT_CARDS.QUEUED'));
    sending.value = false;
    dialog.value.close();
  } catch {
    useAlert(t('WHATSAPP_CONTACT_CARDS.SEND_ERROR'));
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <NextButton
    v-tooltip.top-end="t('WHATSAPP_CONTACT_CARDS.SEND')"
    :aria-label="t('WHATSAPP_CONTACT_CARDS.SEND')"
    icon="i-ph-address-book"
    slate
    faded
    sm
    @click="dialog.open()"
  />
  <Dialog
    ref="dialog"
    :title="t('WHATSAPP_CONTACT_CARDS.SEND')"
    :description="t('WHATSAPP_CONTACT_CARDS.DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_CONTACT_CARDS.SEND')"
    :cancel-button-label="t('WHATSAPP_CONTACT_CARDS.CANCEL')"
    :disable-confirm-button="!valid"
    :is-loading="sending"
    overflow-y-auto
    @confirm="send"
    @close="reset"
  >
    <div class="whatsapp-contact-form grid min-w-0 gap-6">
      <div class="grid gap-3">
        <div class="flex items-end gap-2">
          <Input
            v-model="query"
            :label="t('WHATSAPP_CONTACT_CARDS.SEARCH')"
            :placeholder="t('WHATSAPP_CONTACT_CARDS.SEARCH_PLACEHOLDER')"
            :disabled="sending"
            type="search"
            maxlength="100"
            dir="auto"
            class="min-w-0 flex-1"
            custom-input-class="!h-11 !text-base"
            @keydown.enter.prevent.stop="search"
          />
          <NextButton
            type="button"
            :label="t('WHATSAPP_CONTACT_CARDS.SEARCH_BUTTON')"
            :is-loading="searching"
            :disabled="searching || sending || !query.trim()"
            class="!h-11 shrink-0"
            slate
            faded
            @click="search"
          />
        </div>
        <p
          v-if="searched && !results.length"
          role="status"
          class="m-0 text-sm text-n-slate-11"
        >
          {{ t('WHATSAPP_CONTACT_CARDS.NO_RESULTS') }}
        </p>
        <ul
          v-if="results.length"
          :aria-label="t('WHATSAPP_CONTACT_CARDS.SEARCH')"
          class="m-0 max-h-44 list-none overflow-y-auto rounded-lg border border-n-weak p-1"
        >
          <li v-for="contact in results" :key="contact.id">
            <button
              type="button"
              :disabled="sending"
              class="flex min-h-11 w-full flex-col gap-1 rounded-md p-2 text-start text-n-slate-12 hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              @click="choose(contact)"
            >
              <span dir="auto" class="break-all text-sm font-medium">{{
                contact.name
              }}</span>
              <span dir="ltr" class="text-sm text-n-slate-11">{{
                contact.phone_number
              }}</span>
            </button>
          </li>
        </ul>
      </div>
      <div class="grid gap-4 border-t border-n-weak pt-5">
        <Input
          v-model="name"
          :label="t('WHATSAPP_CONTACT_CARDS.NAME')"
          :disabled="sending"
          type="text"
          maxlength="255"
          required
          dir="auto"
          autocomplete="off"
          custom-input-class="!h-11 !text-base"
        />
        <Input
          v-model="phone"
          :label="t('WHATSAPP_CONTACT_CARDS.PHONE')"
          :disabled="sending"
          maxlength="64"
          required
          type="tel"
          placeholder="+201xxxxxxxxx"
          autocomplete="off"
          custom-input-class="!h-11 !text-base [direction:ltr] text-left"
        />
        <p class="m-0 text-sm leading-6 text-n-slate-11">
          {{ t('WHATSAPP_CONTACT_CARDS.INTERNATIONAL_REQUIRED') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
